import BigNumber from 'bignumber.js'
import { DateTime } from 'luxon'
import { BillingCategoryCode } from './Receipt'

///////////////////////////////////
// Types
///////////////////////////////////

export const DunningPatternCode = {
  None: '00',
  Pattern1: '01',
  Pattern2: '02',
  Pattern3: '03',
  Pattern4: '04',
} as const
export type DunningPatternCode = (typeof DunningPatternCode)[keyof typeof DunningPatternCode]

export const BankAccountDepositType = {
  Savings: 1, // 普通預金
  Checking: 2, // 当座預金
} as const
export type BankAccountDepositType = (typeof BankAccountDepositType)[keyof typeof BankAccountDepositType]

export const textForBankAccountDepositType = (depositType: BankAccountDepositType) => {
  switch (depositType) {
    case BankAccountDepositType.Savings:
      return '普通預金'
    case BankAccountDepositType.Checking:
      return '当座預金'
  }
}

export const HighPressureType = {
  None: 0, // 高圧でない
  WithPowerFactorDiscount: 1, // 力率割引あり
  WithoutPowerFactorDiscount: 2, // 力率割引なし

  TOU: 101, // TOU契約
  HitotoHiroshima: 102, // hitoto広島

  STNetPowerFactor80: 201, // STNet 力率80%
  STNetPowerFactor90: 202, // STNet 力率90%
} as const
export type HighPressureType = (typeof HighPressureType)[keyof typeof HighPressureType]

///////////////////////////////////
// Detail
///////////////////////////////////

export interface DetailData {
  title: string
  unitPrice: string
  count: string
  unit: string
  price: string
  remarks: string
}

export class Detail {
  declare title: string
  declare unitPrice: string
  declare count: string
  declare unit: string
  declare price: string
  declare remarks: string

  constructor(params: DetailData) {
    Object.assign(this, params)
  }
}

///////////////////////////////////
// ReadingValue
///////////////////////////////////

export interface ReadingValueData {
  date: string
  meterNumber: string | null
  preValue: number
  value: number
}

export class ReadingValue {
  declare date: DateTime
  declare meterNumber: string | null
  declare preValue: number
  declare value: number

  constructor(params: ReadingValueData) {
    Object.assign(this, params)
    this.date = DateTime.fromISO(params.date)
  }
}

///////////////////////////////////
// DealingInfo
///////////////////////////////////

type DealingInfoData = {
  date: string
  amount: number
}

class DealingInfo {
  declare date: DateTime
  declare amount: number

  constructor(params: DealingInfoData) {
    Object.assign(this, params)
    this.date = DateTime.fromISO(params.date)
  }
}

///////////////////////////////////
// Invoice
///////////////////////////////////

export type InvoiceData = {
  dailyRateDays: number
  consumption: number
  billingAmount: number
  standardExpiredOn: string | null
  issuingCommissionPrice: number
  dunningDelayAmount: number
  totalAmount: number
  userCode: string | null
  userName: string | null
  moveInOn: string
  moveOutOn: string | null
  userHonorific: string
  isTaxFree: boolean
  basicCapacity: string
  roomType: string
  billingCategoryCode: BillingCategoryCode

  publishedAt: string | null
  roomTypeCode: string
  basicCapacityUnit: string | null
  previousMeterReadOn: string
  meterReadOn: string
  date: string

  calculationInformation: {
    highPressureType: HighPressureType
    threshold1: number
    threshold2: number
    threshold3: number
    basicRateDiscountRate: number
    discountRate1: number
    discountRate2: number
    discountRate3: number
    discountRate4: number
    consumptionTax: number
  }

  unitPrice: {
    step1: number
    step2: number
    step3: number
    step4: number
    basicRateUnitPrice: number
    minimumRenewableEnergy: number
    renewableEnergyUnitPrice: number
    fuelAdjustmentUnitPrice: number
    minimumFuelAdjustment: number
    minimumFeeSystemConsumption: number
  }

  bankAccount: {
    bankName: string
    branchName: string
    depositType: BankAccountDepositType
    number: string
    transferDate: string
  } | null

  readingValue: ReadingValueData
  details: DetailData[]
  dealingInfos: DealingInfoData[]
}

export default class Invoice {
  declare dailyRateDays: number
  declare consumption: number
  declare billingAmount: number
  declare standardExpiredOn: DateTime | null
  declare issuingCommissionPrice: number
  declare dunningDelayAmount: number
  declare totalAmount: number
  declare userCode: string | null
  declare userName: string | null
  declare moveInOn: DateTime
  declare moveOutOn: DateTime | null
  declare userHonorific: string
  declare isTaxFree: boolean
  declare basicCapacity: string
  declare roomType: string
  declare billingCategoryCode: BillingCategoryCode

  declare publishedAt: DateTime | null
  declare roomTypeCode: string
  declare basicCapacityUnit: string | null
  declare previousMeterReadOn: DateTime
  declare meterReadOn: DateTime
  declare date: DateTime

  declare calculationInformation: {
    highPressureType: HighPressureType
    threshold1: number
    threshold2: number
    threshold3: number
    basicRateDiscountRate: number
    discountRate1: number
    discountRate2: number
    discountRate3: number
    discountRate4: number
    consumptionTax: number
  }

  declare unitPrice: {
    step1: number
    step2: number
    step3: number
    step4: number
    basicRateUnitPrice: number
    minimumRenewableEnergy: number
    renewableEnergyUnitPrice: number
    fuelAdjustmentUnitPrice: number
    minimumFuelAdjustment: number
    minimumFeeSystemConsumption: number
  }

  declare bankAccount: {
    bankName: string
    branchName: string
    depositType: BankAccountDepositType
    number: string
    transferDate: DateTime
  } | null

  declare readingValue: ReadingValue
  declare details: Detail[]
  declare dealingInfos: DealingInfo[]

  // Computed properties --------------------------------------------------------------

  get billingCategoryText() {
    switch (this.billingCategoryCode) {
      case BillingCategoryCode.AccountTransfer:
        return '口座振替'
      case BillingCategoryCode.CreditCard:
        return 'クレジットカード払い'
      case BillingCategoryCode.ConvenienceStore:
        return 'コンビニ払い'
      case BillingCategoryCode.DirectTransfer:
        return '銀行振込'
      default:
        return ''
    }
  }

  get originalTotalAmount() {
    return new BigNumber(this.billingAmount)
      .plus(this.issuingCommissionPrice)
      .plus(this.dunningDelayAmount)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber()
  }

  get totalAmountConsumptionTaxPrice() {
    if (this.isTaxFree) return 0

    const tax = this.calculationInformation.consumptionTax

    return new BigNumber(this.originalTotalAmount)
      .div(1 + tax)
      .times(tax)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber()
  }

  get discountRateText() {
    const calcInfo = this.calculationInformation
    const minConsumption = this.unitPrice.minimumFeeSystemConsumption

    const rateSet = new Set<number>()
    if (calcInfo.basicRateDiscountRate > 0) rateSet.add(calcInfo.basicRateDiscountRate)
    if (calcInfo.discountRate1 > 0) rateSet.add(calcInfo.discountRate1)
    if (calcInfo.discountRate2 > 0) rateSet.add(calcInfo.discountRate2)
    if (calcInfo.discountRate3 > 0) rateSet.add(calcInfo.discountRate3)
    if (calcInfo.discountRate4 > 0) rateSet.add(calcInfo.discountRate4)

    if (rateSet.size === 1) {
      // 一律
      const rate = new BigNumber(Array.from(rateSet)[0]).times(100).toNumber()
      return `${rate}%`
    }

    const items: string[] = []

    if (calcInfo.basicRateDiscountRate > 0) {
      const rate = new BigNumber(calcInfo.basicRateDiscountRate).times(100).toNumber()
      items.push(`〜${minConsumption}kWh:${rate}%`)
    }

    if (calcInfo.discountRate1 > 0) {
      const rate = new BigNumber(calcInfo.discountRate1).times(100).toNumber()
      items.push(`${minConsumption + 1}kWh〜${minConsumption + calcInfo.threshold1}kWh:${rate}%`)
    }

    if (calcInfo.discountRate2 > 0) {
      const rate = new BigNumber(calcInfo.discountRate2).times(100).toNumber()
      items.push(`${minConsumption + calcInfo.threshold1 + 1}kWh〜${minConsumption + calcInfo.threshold2}kWh:${rate}%`)
    }

    if (calcInfo.discountRate3 > 0) {
      const rate = new BigNumber(calcInfo.discountRate3).times(100).toNumber()

      if (calcInfo.discountRate4 > 0) {
        items.push(
          `${minConsumption + calcInfo.threshold2 + 1}kWh〜${minConsumption + calcInfo.threshold3}kWh:${rate}%`,
        )
      } else {
        items.push(`${minConsumption + calcInfo.threshold2 + 1}kWh〜:${rate}%`)
      }
    }

    if (calcInfo.discountRate4 > 0) {
      const rate = new BigNumber(calcInfo.discountRate4).times(100).toNumber()
      items.push(`${minConsumption + calcInfo.threshold3 + 1}kWh〜:${rate}%`)
    }

    if (!items.length) items.push('0%')

    return items.join('、')
  }

  // Constructor -----------------------------------------------------------------------

  constructor(params: InvoiceData) {
    Object.assign(this, params)

    this.standardExpiredOn = params.standardExpiredOn ? DateTime.fromISO(params.standardExpiredOn) : null
    this.moveInOn = DateTime.fromISO(params.moveInOn)
    this.moveOutOn = params.moveOutOn ? DateTime.fromISO(params.moveOutOn) : null

    this.publishedAt = params.publishedAt ? DateTime.fromISO(params.publishedAt) : null
    this.previousMeterReadOn = DateTime.fromISO(params.previousMeterReadOn)
    this.meterReadOn = DateTime.fromISO(params.meterReadOn)
    this.date = DateTime.fromISO(params.date)

    this.publishedAt = params.publishedAt ? DateTime.fromISO(params.publishedAt) : null
    this.previousMeterReadOn = DateTime.fromISO(params.previousMeterReadOn)
    this.meterReadOn = DateTime.fromISO(params.meterReadOn)
    this.date = DateTime.fromISO(params.date)

    this.bankAccount = params.bankAccount
      ? {
          ...params.bankAccount,
          transferDate: DateTime.fromISO(params.bankAccount.transferDate),
        }
      : null

    this.readingValue = new ReadingValue(params.readingValue)
    this.details = params.details.map((v) => new Detail(v))
    this.dealingInfos = params.dealingInfos.map((v) => new DealingInfo(v))
  }
}
